import { createMockEventBus, type EventBus } from '@micarahr/event-contracts';
import { createIndexedDbStore, createMemoryStore, type KeyValueStore } from './storage';

/**
 * Service runtime.
 *
 * Everything the services need from outside — the clock, storage, the event
 * bus, artificial latency and induced failures — arrives through here. That is
 * what lets the Simulation Centre make a service slow or make it fail without
 * a single `if (simulating)` inside a service, and what lets a test run the
 * same code with a memory store and a frozen clock.
 */

export interface ServiceRuntime {
  storage: KeyValueStore;
  bus: EventBus;
  /** Server-authoritative time. Never `new Date()` inside a service. */
  now(): string;
  /** Simulated round-trip latency in milliseconds. */
  latencyMs: number;
  /** Services whose next call should fail, by service name. */
  failing: Set<string>;
  /** Global failure switch used by the "service failure" simulation. */
  failAll: boolean;
}

function detectStore(): KeyValueStore {
  if (typeof indexedDB === 'undefined') return createMemoryStore();
  try {
    return createIndexedDbStore();
  } catch {
    return createMemoryStore();
  }
}

export const runtime: ServiceRuntime = {
  storage: detectStore(),
  bus: createMockEventBus(),
  now: () => new Date().toISOString(),
  latencyMs: 120,
  failing: new Set<string>(),
  failAll: false,
};

/** Replace the runtime wholesale. Tests and the Simulation Centre use this. */
export function configureRuntime(patch: Partial<ServiceRuntime>): void {
  Object.assign(runtime, patch);
}

export function delay(ms = runtime.latencyMs): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Deep clone.
 *
 * Services return copies so a component holding a reference cannot mutate the
 * stored record. This is the single rule that keeps "why did that row change
 * when I edited a different one?" out of the application.
 */
export function clone<T>(value: T): T {
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}

/* -------------------------------------------------------------------------- */
/* Errors                                                                     */
/* -------------------------------------------------------------------------- */

export type ServiceErrorCode =
  | 'not_found'
  | 'validation'
  | 'permission_denied'
  | 'conflict'
  | 'precondition_failed'
  | 'storage'
  | 'unavailable'
  | 'session_expired';

/**
 * A domain error.
 *
 * Carries a code the interface can branch on and a message written for the
 * person reading it — not a stack trace, and never an accusation.
 */
export class ServiceError extends Error {
  readonly code: ServiceErrorCode;
  readonly details: Record<string, unknown>;

  constructor(code: ServiceErrorCode, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = 'ServiceError';
    this.code = code;
    this.details = details;
  }
}

export const errors = {
  notFound: (noun: string) =>
    new ServiceError('not_found', `That ${noun} could not be found. It may have been removed.`),
  validation: (message: string, details: Record<string, unknown> = {}) =>
    new ServiceError('validation', message, details),
  permissionDenied: (permission: string) =>
    new ServiceError(
      'permission_denied',
      'Your roles do not include the permission needed for this action.',
      { permission },
    ),
  conflict: (message: string) => new ServiceError('conflict', message),
  precondition: (message: string) => new ServiceError('precondition_failed', message),
  unavailable: (service: string) =>
    new ServiceError(
      'unavailable',
      'MicaraHR could not complete that just now. Nothing has been changed.',
      { service },
    ),
  sessionExpired: () =>
    new ServiceError('session_expired', 'Your session has expired. Please sign in again.'),
};

/**
 * Wrap a service call with latency and induced failure.
 *
 * Every service method goes through this, so the Simulation Centre's failure
 * toggles reach code paths that real users will hit, rather than a mock that
 * only fails in a test.
 */
export async function serviceCall<T>(service: string, work: () => Promise<T> | T): Promise<T> {
  await delay();
  if (runtime.failAll || runtime.failing.has(service)) {
    throw errors.unavailable(service);
  }
  return work();
}
